// Target utilities for handling different target types
export const targetUtils = {
  getTargetIcon: (type: string): string => {
    const iconMap: Record<string, string> = {
      'single': 'fas fa-server',
      'hostname': 'fas fa-globe',
      'range': 'fas fa-network-wired',
      'cidr': 'fas fa-sitemap'
    };
    return iconMap[type] || 'fas fa-bullseye';
  },

  getTargetColor: (type: string): string => {
    const colorMap: Record<string, string> = {
      'single': '#3b82f6',
      'hostname': '#10b981',
      'range': '#f59e0b',
      'cidr': '#8b5cf6'
    };
    return colorMap[type] || '#6b7280';
  },

  parseRange: (address: string): number => {
    try {
      const parts = address.split('-');
      if (parts.length !== 2) return 1;

      const startIP = parts[0].trim();
      const endPart = parts[1].trim();

      // Support both full IP range (192.168.1.1-192.168.1.255) and short format (192.168.1.1-255)
      let endIP = endPart;
      if (!endPart.includes('.')) {
        // Short format: 192.168.1.1-255 means 192.168.1.1 to 192.168.1.255
        const startParts = startIP.split('.');
        if (startParts.length === 4) {
          startParts[3] = endPart;
          endIP = startParts.join('.');
        }
      }

      const startNum = targetUtils.ipToNumber(startIP);
      const endNum = targetUtils.ipToNumber(endIP);

      if (startNum === null || endNum === null || endNum < startNum) {
        return 1;
      }

      return endNum - startNum + 1;
    } catch (error) {
      console.error('Error parsing IP range:', error);
      return 1;
    }
  },

  parseCIDR: (address: string): number => {
    try {
      const match = address.match(/\/(\d+)$/);
      if (!match) return 1;

      const prefixLength = parseInt(match[1]);
      if (prefixLength < 0 || prefixLength > 32) return 1;

      const hostBits = 32 - prefixLength;
      let hostCount = Math.pow(2, hostBits);

      if (prefixLength < 31) {
        hostCount -= 2;
      }

      return Math.max(0, hostCount);
    } catch (error) {
      console.error('Error parsing CIDR:', error);
      return 1;
    }
  },

  ipToNumber: (ip: string): number | null => {
    try {
      if (!targetUtils.isValidIP(ip)) return null;

      const parts = ip.split('.').map(part => parseInt(part, 10));
      return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
    } catch (error) {
      return null;
    }
  },

  isValidIP: (ip: string): boolean => {
    if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip)) return false;

    const parts = ip.split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255 && part === num.toString();
    });
  },

  isIPAddress: (str: string): boolean => {
    return /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(str);
  },

  // Check if string is a valid short IP range format like 192.168.1.1-255
  isShortIPRange: (str: string): boolean => {
    const parts = str.split('-');
    if (parts.length !== 2) return false;

    const startIP = parts[0].trim();
    const endPart = parts[1].trim();

    // Start must be a full IP address
    if (!targetUtils.isIPAddress(startIP)) return false;

    // End can be either a full IP or just the last octet (1-255)
    if (targetUtils.isIPAddress(endPart)) return true; // Full IP-IP range

    // Check if endPart is a valid last octet (0-255)
    const endNum = parseInt(endPart, 10);
    return !isNaN(endNum) && endNum >= 0 && endNum <= 255 && endPart === endNum.toString();
  },

  // Normalize short IP range to full format (192.168.1.1-255 -> 192.168.1.1-192.168.1.255)
  normalizeIPRange: (address: string): string => {
    const parts = address.split('-');
    if (parts.length !== 2) return address;

    const startIP = parts[0].trim();
    const endPart = parts[1].trim();

    // If endPart is already a full IP, return as-is
    if (endPart.includes('.')) return address;

    // Convert short format to full format
    const startParts = startIP.split('.');
    if (startParts.length === 4) {
      startParts[3] = endPart;
      return `${startIP}-${startParts.join('.')}`;
    }

    return address;
  },

  isValidCIDR: (cidr: string): boolean => {
    const match = cidr.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\/(\d{1,2})$/);
    if (!match) return false;

    const [, ip, prefix] = match;
    const prefixNum = parseInt(prefix, 10);

    return targetUtils.isValidIP(ip) && prefixNum >= 0 && prefixNum <= 32;
  },

  isValidHostname: (hostname: string): boolean => {
    if (hostname.length > 253) return false;

    const labelRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
    const labels = hostname.split('.');

    return labels.every(label => label.length > 0 && labelRegex.test(label));
  }
};
